import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

const DropdownWrapper = styled.div`
  position: relative;
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
  overflow: visible;
`;

const DiscountUnitButton = styled.button`
  width: ${props => (props.fullWidth ? '100%' : 'auto')};
  min-width: ${props => (props.fullWidth ? '0' : '44px')};
  min-height: 44px;
  padding: var(--sp-2) var(--sp-3);
  border: 1px solid var(--input-border);
  border-radius: var(--r-sm);
  background: ${props => (props.fullWidth ? 'var(--input-bg)' : 'transparent')};
  color: var(--text);
  font-size: var(--fs-base);
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  box-sizing: border-box;
  position: relative;
  z-index: var(--z-dropdown);
  transition: background .15s, color .15s, box-shadow .15s, transform .08s;

  svg { color: var(--text-muted); flex-shrink: 0; transition: color .2s; }

  &:hover { background: var(--table-row-hover); transform: translateY(-1px); }
  &:focus-visible { outline: none; box-shadow: var(--focus-ring); }
  &[data-open="true"] { background: var(--card-bg); svg { color: var(--primary); } }
`;

const FilterOptionsList = styled.ul`
  position: absolute;
  top: calc(100% - 6px);
  right: 0;
  min-width: 64px;
  max-width: 220px;
  max-height: 220px;
  margin: 0;
  padding: var(--sp-1);
  list-style: none;
  background: var(--card-bg);
  border: 1px solid var(--input-border);
  border-radius: var(--r-md);
  box-shadow: 0 8px 32px var(--shadow-color);
  overflow-y: auto;
  z-index: var(--z-popover);
`;

const FilterOptionItem = styled.li`
  padding: var(--sp-3);

  /* Opcje „%” i „zł” mialy 38px wysokosci (iPad 27.09.2026). */
  @media (max-width: 768px), (hover: none) and (pointer: coarse) {
    min-height: 44px;
    display: flex;
    align-items: center;
  }
  border-radius: var(--r-sm);
  font-size: var(--fs-base);
  font-weight: 500;
  color: var(--text);
  cursor: pointer;
  transition: background .15s, color .15s;

  .option-content { display:flex; align-items:center; gap:8px; }
  &:hover { background: var(--table-row-hover); }
  &[data-selected="true"] { background: rgba(59,130,246,0.15); color: var(--primary); font-weight:600; }
`;

const DotIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5" cy="5" r="5" fill="currentColor" />
  </svg>
);

export default function DiscountUnitDropdown({ value = '%', onChange = () => {}, options: propOptions, fullWidth = false }) {
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState(null);
  const wrapperRef = useRef(null);
  const buttonRef = useRef(null);
  const portalRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      const target = e.target;
      const insideWrapper = wrapperRef.current && wrapperRef.current.contains(target);
      const insidePortal = portalRef.current && portalRef.current.contains(target);
      if (!insideWrapper && !insidePortal) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    document.addEventListener('scroll', handler, true);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('scroll', handler, true); };
  }, [open]);

  const defaultOptions = [{ value: '%', label: '%' }, { value: 'PLN', label: 'PLN' }];
  const options = Array.isArray(propOptions) && propOptions.length ? propOptions : defaultOptions;
  const selected = options.find(o => o.value === value) || options[0];

  const toggleOpen = () => {
    if (!open) {
      const el = buttonRef.current || wrapperRef.current;
      if (el) setAnchorRect(el.getBoundingClientRect());
    }
    setOpen(o => !o);
  };

  const renderList = (ref) => {
    const width = anchorRect ? (fullWidth ? Math.round(anchorRect.width) : Math.min(Math.max(64, anchorRect.width), 140)) : 120;
    const style = anchorRect ? {
      position: 'fixed',
      left: `${Math.round(anchorRect.right - width)}px`,
      top: `${Math.round(anchorRect.bottom - 6)}px`,
      width: `${width}px`,
      zIndex: 16200
    } : { position: 'fixed', zIndex: 16200 };

    return (
      <FilterOptionsList role="listbox" ref={ref} style={style}>
        {options.map(o => {
          const sel = o.value === value;
          return (
            <FilterOptionItem key={o.value} data-selected={sel} onClick={() => { onChange(o.value); setOpen(false); }}>
              <span className="option-content">
                {sel && <DotIcon />}
                <span className="option-label">{o.label}</span>
              </span>
            </FilterOptionItem>
          );
        })}
      </FilterOptionsList>
    );
  };

  return (
    <DropdownWrapper ref={wrapperRef} fullWidth={fullWidth}>
      <DiscountUnitButton
        ref={buttonRef}
        type="button"
        data-open={open}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={toggleOpen}
        fullWidth={fullWidth}
      >
        <span style={{ textAlign: 'left', flex: 1 }}>{selected.label}</span>
        {open ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M18 15l-6-6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        )}
      </DiscountUnitButton>

      {open && anchorRect ? ReactDOM.createPortal(renderList(portalRef), document.body) : null}
    </DropdownWrapper>
  );
}
